export type NotificationLifecycleType =
  | 'welcome'
  | 'restock'
  | 'restock_4_weeks'
  | 'cart';

export type NotificationTarget =
  | {
      href: string;
      isWaitingForPayment: boolean;
      kind: 'order';
      orderMetaId: string;
      warehouse: string;
    }
  | {
      href: string;
      isWaitingForPayment: false;
      kind: 'recipe';
    }
  | {
      href: string;
      isWaitingForPayment: false;
      kind: 'lifecycle';
      lifecycleType: NotificationLifecycleType;
    }
  | {
      href: string;
      isWaitingForPayment: false;
      kind: 'unknown';
    };

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const LIFECYCLE_HREFS: Record<NotificationLifecycleType, string> = {
  welcome: '/',
  restock: '/',
  restock_4_weeks: '/',
  cart: '/cart',
};

const isNotificationLifecycleType = (
  value: unknown
): value is NotificationLifecycleType =>
  value === 'welcome' ||
  value === 'restock' ||
  value === 'restock_4_weeks' ||
  value === 'cart';

const buildBaseOrderDetailHref = (orderMetaId: string, warehouse: string) =>
  `/order-detail/${encodeURIComponent(
    orderMetaId
  )}?warehouse=${encodeURIComponent(warehouse)}`;

export function parseNotificationTarget(
  metadata: Record<string, unknown>
): NotificationTarget {
  if (isNotificationLifecycleType(metadata.lifecycle_type)) {
    return {
      href: LIFECYCLE_HREFS[metadata.lifecycle_type],
      isWaitingForPayment: false,
      kind: 'lifecycle',
      lifecycleType: metadata.lifecycle_type,
    };
  }

  if (isNonEmptyString(metadata.recipe_id) && isNonEmptyString(metadata.slug)) {
    return {
      href: `/recipes/${encodeURIComponent(metadata.slug)}`,
      isWaitingForPayment: false,
      kind: 'recipe',
    };
  }

  if (
    isNonEmptyString(metadata.order_meta_id) &&
    isNonEmptyString(metadata.warehouse_id)
  ) {
    return {
      href: buildBaseOrderDetailHref(
        metadata.order_meta_id,
        metadata.warehouse_id
      ),
      isWaitingForPayment: metadata.is_waiting_for_payment === 'true',
      kind: 'order',
      orderMetaId: metadata.order_meta_id,
      warehouse: metadata.warehouse_id,
    };
  }

  return {
    href: '/notification',
    isWaitingForPayment: false,
    kind: 'unknown',
  };
}

export function parseNotificationRedirect(metadata: Record<string, unknown>) {
  return parseNotificationTarget(metadata).href;
}
